import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  DirectionsCar as DirectionsCarIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import UbicacionSearch from '../../components/UbicacionSearch';
import TransporteSearch from '../../components/TransporteSearch';
import VendedorSelect from '../../components/VendedorSelect';
import UnidadCrudDialog from '../../components/servicio/UnidadCrudDialog';
import PDFViewer from '../../components/shared/PDFViewer';
import clienteService, { OPTIONS_CLIENTE_OPERATIVO } from '../../services/clienteService';
import productoService, {
  OPTIONS_PRODUCTO_OPERATIVO,
  PARAMS_PRODUCTO_OPERATIVO,
  Producto,
} from '../../services/productoService';
import ordenReparacionService, {
  CreateOrdenReparacionDto,
  OrdenReparacionItemDto,
  OrdenReparacionReadDto,
} from '../../services/ordenReparacionService';
import { ROUTES } from '../../config/routes';
import { getApiErrorMessage } from '../../utils/apiError';
import { labelEstadoOrdenTrabajo, ordenTrabajoEsEditable } from '../../utils/ordenTrabajoFactura';
import { IUbicacion } from '../../interfaces/Ubicacion';

const fieldGrande = {
  '& .MuiInputBase-root': { fontSize: '1.05rem', minHeight: 48 },
  '& .MuiInputLabel-root': { fontSize: '1rem' },
};

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const nowHora = (): string => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const PRODUCTOS_ROWS_PER_PAGE = 10;

const emptyLinea = (): OrdenReparacionItemDto => ({
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  unidadMedida: 'UNI',
});

const OrdenReparacionForm: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id: idParam } = useParams();
  const idDecoded = idParam ? decodeURIComponent(idParam) : '';
  const isEdit = !!(idParam && idParam !== 'nuevo');
  const formRef = useRef<HTMLFormElement>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [nroCliente, setNroCliente] = useState<number | null>(null);
  const [nombreCliente, setNombreCliente] = useState('');
  const [direccionCliente, setDireccionCliente] = useState('');
  const [nroDepositoOrigen, setNroDepositoOrigen] = useState<number | null>(null);
  const [nombreDepositoOrigen, setNombreDepositoOrigen] = useState('');
  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [nroTransporte, setNroTransporte] = useState<number | string | null>(null);
  const [nombreTransporte, setNombreTransporte] = useState('');
  const [serie, setSerie] = useState('A');
  const [numero, setNumero] = useState('');
  const [fechaEmision, setFechaEmision] = useState(todayIso);
  const [horaEmision, setHoraEmision] = useState(nowHora);
  const [fechaVence, setFechaVence] = useState(todayIso);
  const [detalleReparacion, setDetalleReparacion] = useState('');
  const [patente, setPatente] = useState('');
  const [items, setItems] = useState<OrdenReparacionItemDto[]>([]);
  const [lineaNueva, setLineaNueva] = useState<OrdenReparacionItemDto>(emptyLinea());
  const [ordenCargada, setOrdenCargada] = useState<OrdenReparacionReadDto | null>(null);
  const codigoLineaRef = useRef<HTMLInputElement | null>(null);

  const soloLectura = ordenCargada != null && !ordenTrabajoEsEditable(ordenCargada);

  const [openClienteSearch, setOpenClienteSearch] = useState(false);
  const [openDepositoSearch, setOpenDepositoSearch] = useState(false);
  const [openTransporteSearch, setOpenTransporteSearch] = useState(false);
  const [openProductoSearch, setOpenProductoSearch] = useState(false);
  const [openUnidadSearch, setOpenUnidadSearch] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [productoSearchTerm, setProductoSearchTerm] = useState('');
  const [productos, setProductos] = useState<Producto[]>([]);
  const [productosPage, setProductosPage] = useState(0);
  const [productosTotalCount, setProductosTotalCount] = useState(0);
  const [loadingProductos, setLoadingProductos] = useState(false);

  const otrosDialogsOpen = openClienteSearch || openDepositoSearch || openTransporteSearch || openUnidadSearch;
  const dialogsOpen = otrosDialogsOpen || openProductoSearch;

  const clienteDisplay =
    nroCliente != null ? `${nroCliente}${nombreCliente ? ` - ${nombreCliente}` : ''}` : '';

  const loadProximoNumero = useCallback(async () => {
    try {
      const n = await ordenReparacionService.getProximoNumero(serie);
      if (n) setNumero(n);
    } catch {
      enqueueSnackbar('No se pudo obtener el próximo número de orden', { variant: 'warning' });
    }
  }, [serie, enqueueSnackbar]);

  const loadOrden = useCallback(async () => {
    if (!isEdit || !idDecoded) return;
    try {
      setLoading(true);
      const o = await ordenReparacionService.getOrden(idDecoded);
      setOrdenCargada(o);
      setNroCliente(o.nroCliente);
      setNombreCliente(o.nombreCliente || '');
      setDireccionCliente(o.direccionCliente || '');
      setNroDepositoOrigen(o.nroDepositoOrigen);
      setNombreDepositoOrigen(o.nombreDepositoOrigen || '');
      setNroVendedor(o.nroVendedor);
      setNroTransporte(o.nroTransporte);
      setNombreTransporte(o.nombreTransporte || '');
      setSerie(o.serie || 'A');
      setNumero(o.numero || '');
      const emision = o.fechaEmision?.slice(0, 10) || todayIso();
      setFechaEmision(emision);
      setHoraEmision(o.horaEmision || nowHora());
      setFechaVence(o.fechaVence?.slice(0, 10) || emision);
      setDetalleReparacion(o.detalleReparacion || '');
      setPatente(o.patente || '');
      setItems(o.items ?? []);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar la orden'), { variant: 'error' });
      navigate(ROUTES.servicio.ordenesTrabajo.list);
    } finally {
      setLoading(false);
    }
  }, [isEdit, idDecoded, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isEdit) loadOrden();
    else loadProximoNumero();
  }, [isEdit, loadOrden, loadProximoNumero]);

  const handleClienteSelect = async (cliente: {
    nroCliente: number;
    nombre: string;
    razonSocial?: string;
    direccion?: string;
  }) => {
    setNroCliente(cliente.nroCliente);
    setNombreCliente(cliente.razonSocial || cliente.nombre || '');
    setPatente('');
    setOpenClienteSearch(false);
    try {
      const c = await clienteService.getCliente(cliente.nroCliente, OPTIONS_CLIENTE_OPERATIVO);
      setDireccionCliente(c.direccion || cliente.direccion || '');
    } catch {
      setDireccionCliente(cliente.direccion || '');
    }
  };

  const loadProductos = useCallback(async () => {
    if (!openProductoSearch) return;
    try {
      setLoadingProductos(true);
      const res = await productoService.getProductos({
        searchTerm: productoSearchTerm || undefined,
        pageNumber: productosPage + 1,
        pageSize: PRODUCTOS_ROWS_PER_PAGE,
        ...PARAMS_PRODUCTO_OPERATIVO,
      });
      setProductos(res.items ?? []);
      setProductosTotalCount(res.totalCount ?? 0);
    } catch {
      setProductos([]);
      setProductosTotalCount(0);
      enqueueSnackbar('Error al buscar productos', { variant: 'error' });
    } finally {
      setLoadingProductos(false);
    }
  }, [openProductoSearch, productoSearchTerm, productosPage, enqueueSnackbar]);

  useEffect(() => {
    if (openProductoSearch) void loadProductos();
  }, [openProductoSearch, loadProductos]);

  const focusCodigoLinea = useCallback(() => {
    window.setTimeout(() => codigoLineaRef.current?.focus(), 0);
  }, []);

  const pushLinea = useCallback(
    (linea: OrdenReparacionItemDto) => {
      const codigo = linea.codigoArticulo.trim();
      const descripcion = linea.descripcion.trim();
      if (!codigo && !descripcion) {
        enqueueSnackbar('Ingresá código o descripción del repuesto', { variant: 'warning' });
        return false;
      }
      const cantidad = Number(linea.cantidad) > 0 ? Number(linea.cantidad) : 1;
      setItems((prev) => [
        ...prev,
        {
          codigoArticulo: codigo,
          descripcion,
          cantidad,
          unidadMedida: linea.unidadMedida.trim() || 'UNI',
        },
      ]);
      setLineaNueva(emptyLinea());
      focusCodigoLinea();
      return true;
    },
    [enqueueSnackbar, focusCodigoLinea],
  );

  const handleSelectProducto = (p: Producto) => {
    const codigo = p.codigoArticulo || (p as { codigo?: string }).codigo || '';
    const desc = p.descripcion || (p as { descripcionArticulo?: string }).descripcionArticulo || '';
    const um =
      String((p as { um?: string; unidadMedida?: string }).um || (p as { unidadMedida?: string }).unidadMedida || 'UNI').trim() ||
      'UNI';
    setOpenProductoSearch(false);
    pushLinea({
      codigoArticulo: String(codigo),
      descripcion: String(desc),
      cantidad: lineaNueva.cantidad > 0 ? lineaNueva.cantidad : 1,
      unidadMedida: um,
    });
  };

  const abrirBusquedaProducto = (term?: string) => {
    setProductoSearchTerm(term ?? lineaNueva.codigoArticulo);
    setProductosPage(0);
    setOpenProductoSearch(true);
  };

  const handleCodigoEnter = useCallback(
    async (codigoValue: string) => {
      const codigo = codigoValue.trim();
      if (!codigo) return;
      try {
        const p = await productoService.getProducto(codigo, OPTIONS_PRODUCTO_OPERATIVO);
        const desc = p.descripcion || '';
        const um =
          String((p as { um?: string; unidadMedida?: string }).um || (p as { unidadMedida?: string }).unidadMedida || 'UNI').trim() ||
          'UNI';
        pushLinea({
          codigoArticulo: codigo,
          descripcion: desc,
          cantidad: lineaNueva.cantidad > 0 ? lineaNueva.cantidad : 1,
          unidadMedida: um,
        });
      } catch {
        enqueueSnackbar(`No se encontró el producto con código "${codigo}"`, { variant: 'warning' });
        setProductoSearchTerm(codigo);
        setProductosPage(0);
        setOpenProductoSearch(true);
      }
    },
    [enqueueSnackbar, lineaNueva.cantidad, pushLinea],
  );

  const actualizarCantidadItem = (index: number, cantidad: number) => {
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, cantidad: cantidad > 0 ? cantidad : 1 } : it)),
    );
  };

  const quitarLinea = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const buildPayload = (): CreateOrdenReparacionDto => ({
    nroCliente: nroCliente ?? 0,
    nombreCliente: nombreCliente.trim() || null,
    direccionCliente: direccionCliente.trim() || null,
    nroDepositoOrigen,
    nombreDepositoOrigen: nombreDepositoOrigen.trim() || null,
    nroVendedor,
    nroTransporte,
    nombreTransporte: nombreTransporte.trim() || null,
    serie: serie.trim() || 'A',
    numero: numero.trim(),
    fechaEmision,
    horaEmision,
    fechaVence,
    detalleReparacion: detalleReparacion.trim(),
    patente: patente.trim() || null,
    items,
  });

  const handleCancel = useCallback(() => {
    if (saving) return;
    navigate(ROUTES.servicio.ordenesTrabajo.list);
  }, [navigate, saving]);

  const handleImprimir = useCallback(async () => {
    if (!isEdit || !idDecoded) {
      enqueueSnackbar('Guardá la orden antes de imprimir', { variant: 'warning' });
      return;
    }
    try {
      setPdfLoading(true);
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      const blob = await ordenReparacionService.getPdf(idDecoded);
      setPdfUrl(window.URL.createObjectURL(blob));
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al generar el PDF'), { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  }, [isEdit, idDecoded, pdfUrl, enqueueSnackbar]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || soloLectura) return;
    try {
      setSaving(true);
      if (!nroCliente) {
        enqueueSnackbar('Seleccioná un cliente', { variant: 'warning' });
        return;
      }
      if (!detalleReparacion.trim()) {
        enqueueSnackbar('El detalle de reparación es requerido', { variant: 'warning' });
        return;
      }
      const payload = buildPayload();
      if (isEdit) {
        await ordenReparacionService.updateOrden(idDecoded, payload);
      } else {
        await ordenReparacionService.createOrden(payload);
      }
      enqueueSnackbar('Orden guardada exitosamente', { variant: 'success' });
      navigate(ROUTES.servicio.ordenesTrabajo.list);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar la orden'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        if (dialogsOpen) return;
        e.preventDefault();
        if (!saving && !soloLectura) formRef.current?.requestSubmit();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        if (openProductoSearch) {
          setOpenProductoSearch(false);
          return;
        }
        if (otrosDialogsOpen) return;
        handleCancel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleCancel, saving, soloLectura, dialogsOpen, openProductoSearch, otrosDialogsOpen]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <form ref={formRef} onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ textTransform: 'none' }}>
                  Órdenes de trabajo
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {isEdit ? 'Orden de trabajo' : 'Nueva orden de trabajo'}
                </Typography>
                {ordenCargada && (
                  <Chip
                    label={labelEstadoOrdenTrabajo(ordenCargada)}
                    color={soloLectura ? 'success' : 'warning'}
                    variant="outlined"
                  />
                )}
              </Stack>

              {soloLectura && (
                <Alert severity="info">
                  Esta orden ya fue facturada. Podés consultar los datos pero no modificarlos.
                </Alert>
              )}

              {/* Cabecera cliente / logística */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Cliente"
                      fullWidth
                      value={clienteDisplay}
                      placeholder="Buscar cliente..."
                      sx={fieldGrande}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              color="primary"
                              disabled={soloLectura}
                              onClick={() => setOpenClienteSearch(true)}
                              size="small"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onClick={() => {
                        if (!soloLectura) setOpenClienteSearch(true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Dirección"
                      fullWidth
                      value={direccionCliente}
                      onChange={(e) => setDireccionCliente(e.target.value)}
                      disabled={soloLectura}
                      sx={fieldGrande}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Unidad"
                      fullWidth
                      value={patente}
                      placeholder="Unidad a la que se dará servicio"
                      disabled={soloLectura}
                      sx={fieldGrande}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <DirectionsCarIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            {patente && !soloLectura ? (
                              <IconButton
                                aria-label="Quitar unidad"
                                onClick={() => setPatente('')}
                                size="small"
                              >
                                <CloseIcon />
                              </IconButton>
                            ) : null}
                            <IconButton
                              color="primary"
                              disabled={soloLectura}
                              onClick={() => {
                                if (!nroCliente) {
                                  enqueueSnackbar('Seleccioná primero un cliente', { variant: 'warning' });
                                  return;
                                }
                                setOpenUnidadSearch(true);
                              }}
                              size="small"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onClick={() => {
                        if (soloLectura) return;
                        if (!nroCliente) {
                          enqueueSnackbar('Seleccioná primero un cliente', { variant: 'warning' });
                          return;
                        }
                        setOpenUnidadSearch(true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Depósito origen"
                      fullWidth
                      value={
                        nroDepositoOrigen
                          ? `${nroDepositoOrigen}${nombreDepositoOrigen ? ` - ${nombreDepositoOrigen}` : ''}`
                          : ''
                      }
                      placeholder="Seleccionar..."
                      sx={fieldGrande}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              color="primary"
                              disabled={soloLectura}
                              onClick={() => setOpenDepositoSearch(true)}
                              size="small"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onClick={() => {
                        if (!soloLectura) setOpenDepositoSearch(true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <VendedorSelect
                      value={nroVendedor}
                      onChange={(nro) => setNroVendedor(nro)}
                      label="Vendedor"
                      size="medium"
                      disabled={soloLectura}
                      sx={fieldGrande}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Transporte"
                      fullWidth
                      value={
                        nroTransporte
                          ? `${nroTransporte}${nombreTransporte ? ` - ${nombreTransporte}` : ''}`
                          : ''
                      }
                      placeholder="Opcional..."
                      sx={fieldGrande}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              color="primary"
                              disabled={soloLectura}
                              onClick={() => setOpenTransporteSearch(true)}
                              size="small"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onClick={() => {
                        if (!soloLectura) setOpenTransporteSearch(true);
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Orden servicio + fechas */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="overline" color="text.secondary">
                      Orden servicio
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {serie} {numero || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Fecha emisión"
                      type="date"
                      fullWidth
                      value={fechaEmision}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFechaEmision(v);
                        if (!isEdit) setFechaVence(v);
                      }}
                      InputLabelProps={{ shrink: true }}
                      disabled={soloLectura}
                      sx={fieldGrande}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Hora emisión"
                      type="time"
                      fullWidth
                      value={horaEmision}
                      onChange={(e) => setHoraEmision(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={soloLectura}
                      sx={fieldGrande}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Fecha vence"
                      type="date"
                      fullWidth
                      value={fechaVence}
                      onChange={(e) => setFechaVence(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={soloLectura}
                      sx={fieldGrande}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Detalle reparación */}
              <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1, bgcolor: '#fff9c4', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Detalle de reparación
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <TextField
                    multiline
                    minRows={4}
                    fullWidth
                    placeholder="Describí el trabajo a realizar..."
                    value={detalleReparacion}
                    onChange={(e) => setDetalleReparacion(e.target.value)}
                    disabled={soloLectura}
                    sx={{
                      ...fieldGrande,
                      '& .MuiInputBase-root': { fontSize: '1.1rem', lineHeight: 1.5 },
                    }}
                  />
                </Box>
              </Paper>

              {/* Repuestos / materiales */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                  Repuestos y materiales
                </Typography>
                <TableContainer>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', width: 100 }} align="right">
                          Cant.
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', width: 80 }}>UM</TableCell>
                        <TableCell sx={{ width: 56 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, idx) => (
                        <TableRow
                          key={`${item.codigoArticulo}-${idx}`}
                          sx={{
                            '&:hover': {
                              bgcolor: '#f9f9f9',
                            },
                          }}
                        >                          <TableCell sx={{ fontSize: '1rem' }}>{item.codigoArticulo}</TableCell>
                          <TableCell sx={{ fontSize: '1rem' }}>{item.descripcion}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '1rem' }}>
                            {soloLectura ? (
                              item.cantidad
                            ) : (
                              <TextField
                                size="small"
                                type="number"
                                value={item.cantidad}
                                onChange={(e) => actualizarCantidadItem(idx, Number(e.target.value) || 0)}
                                inputProps={{ min: 1, step: 1 }}
                                sx={{ width: 90 }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '1rem' }}>{item.unidadMedida}</TableCell>
                          <TableCell>
                            {!soloLectura && (
                              <IconButton color="error" onClick={() => quitarLinea(idx)} size="small">
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!soloLectura && (
                      <TableRow sx={{ bgcolor: '#f8fbff' }}>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="Código"
                            inputRef={codigoLineaRef}
                            value={lineaNueva.codigoArticulo}
                            onChange={(e) =>
                              setLineaNueva((p) => ({ ...p, codigoArticulo: e.target.value.toUpperCase() }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void handleCodigoEnter((e.target as HTMLInputElement).value);
                              }
                            }}
                            sx={{ minWidth: 140, ...fieldGrande }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => abrirBusquedaProducto(lineaNueva.codigoArticulo)}
                                  >
                                    <SearchIcon />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {lineaNueva.descripcion || 'Buscá un artículo'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={lineaNueva.cantidad}
                            onChange={(e) =>
                              setLineaNueva((p) => ({ ...p, cantidad: Number(e.target.value) || 0 }))
                            }
                            inputProps={{ min: 1, step: 1 }}
                            sx={{ width: 90, ...fieldGrande }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void handleCodigoEnter(lineaNueva.codigoArticulo);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{lineaNueva.unidadMedida || '—'}</Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Stack direction="row" spacing={2} sx={{ pt: 1 }} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                {isEdit ? (
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={pdfLoading ? <CircularProgress size={18} /> : <PrintIcon />}
                    onClick={() => void handleImprimir()}
                    disabled={pdfLoading || saving}
                    sx={{ textTransform: 'none' }}
                  >
                    Imprimir
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || soloLectura}
                  sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none' }}
                >
                  {saving ? <CircularProgress size={24} /> : 'F2-Aceptar'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={saving}
                  sx={{ textTransform: 'none' }}
                >
                  ESC-Cancelar
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ClienteSearch open={openClienteSearch} onClose={() => setOpenClienteSearch(false)} onSelect={handleClienteSelect} />

      <UnidadCrudDialog
        open={openUnidadSearch}
        onClose={() => setOpenUnidadSearch(false)}
        onSelect={(item) => {
          setPatente(item.codigo);
          setOpenUnidadSearch(false);
        }}
        nroCliente={nroCliente ?? 0}
      />

      {pdfUrl && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={() => {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
          }}
          title={`Orden de trabajo ${serie} ${numero}`.trim()}
          documentId={idDecoded || 'orden-trabajo'}
          documentType="informeCliente"
          downloadFileName={`orden-trabajo-${idDecoded || 'nueva'}.pdf`}
        />
      )}

      <UbicacionSearch
        open={openDepositoSearch}
        onClose={() => setOpenDepositoSearch(false)}
        onSelect={(u: IUbicacion) => {
          setNroDepositoOrigen(u.nroUbicacion);
          setNombreDepositoOrigen(u.nombreUbicacion || '');
          setOpenDepositoSearch(false);
        }}
      />

      <TransporteSearch
        open={openTransporteSearch}
        onClose={() => setOpenTransporteSearch(false)}
        onSelect={(t) => {
          setNroTransporte(t.nroExpreso ?? null);
          setNombreTransporte(t.nombreExpreso || t.nombre || '');
          setOpenTransporteSearch(false);
        }}
      />

      <Dialog
        open={openProductoSearch}
        onClose={() => setOpenProductoSearch(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            pb: 1,
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Buscar artículo
          </Typography>
          <IconButton aria-label="Cerrar" onClick={() => setOpenProductoSearch(false)} edge="end" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código o descripción..."
              value={productoSearchTerm}
              onChange={(e) => setProductoSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setProductosPage(0);
                  void loadProductos();
                }
              }}
              sx={fieldGrande}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              size="small"
              fullWidth={isMobile}
              onClick={() => {
                setProductosPage(0);
                void loadProductos();
              }}
              disabled={loadingProductos}
              sx={{ color: '#1976d2', borderColor: '#1976d2', flexShrink: 0 }}
            >
              Buscar
            </Button>
          </Stack>

          {loadingProductos ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {productos.length === 0 ? (
                <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
                  Sin resultados.
                </Typography>
              ) : (
                productos.map((p) => (
                  <Card
                    key={p.codigoArticulo}
                    variant="outlined"
                    onClick={() => handleSelectProducto(p)}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}>
                        {p.codigoArticulo}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5, wordBreak: 'break-word' }}>
                        {p.descripcion}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectProducto(p);
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, minHeight: 48 }}
                      >
                        Seleccionar
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          ) : (
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="medium">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Seleccionar
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        Sin resultados
                      </TableCell>
                    </TableRow>
                  ) : (
                    productos.map((p) => (
                      <TableRow
                        key={p.codigoArticulo}
                        onClick={() => handleSelectProducto(p)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell sx={{ fontSize: '1rem' }}>{p.codigoArticulo}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{p.descripcion}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectProducto(p);
                            }}
                          >
                            Seleccionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={productosTotalCount}
            page={productosPage}
            onPageChange={(_, p) => setProductosPage(p)}
            rowsPerPage={PRODUCTOS_ROWS_PER_PAGE}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[PRODUCTOS_ROWS_PER_PAGE]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              mt: 1,
              '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            size="small"
            fullWidth={isMobile}
            onClick={() => setOpenProductoSearch(false)}
            sx={{ textTransform: 'none' }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdenReparacionForm;
